import { create } from "zustand";
import type { GatewayEventFrame, GatewayHelloOk } from "../gateway";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

export type Channel = {
  id: string;
  type: string;
  name: string;
  enabled: boolean;
  status: "online" | "offline" | "error";
  config?: Record<string, unknown>;
};

export type Session = {
  id: string;
  key: string;
  userId?: string;
  channelId?: string;
  startedAt: number;
  lastMessageAt?: number;
  messageCount: number;
};

export type Skill = {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
};

export type LogEntry = {
  id: string;
  level: "debug" | "info" | "warn" | "error";
  message: string;
  timestamp: number;
  source?: string;
  data?: unknown;
};

export type GatewayState = {
  // Connection
  status: ConnectionStatus;
  hello: GatewayHelloOk | null;
  gatewayUrl: string;
  token: string;

  // Data
  channels: Channel[];
  sessions: Session[];
  skills: Skill[];
  logs: LogEntry[];

  // Actions
  setStatus: (status: ConnectionStatus) => void;
  setHello: (hello: GatewayHelloOk | null) => void;
  setGatewayUrl: (url: string) => void;
  setToken: (token: string) => void;

  setChannels: (channels: Channel[]) => void;
  updateChannel: (id: string, updates: Partial<Channel>) => void;

  setSessions: (sessions: Session[]) => void;
  addSession: (session: Session) => void;
  removeSession: (id: string) => void;

  setSkills: (skills: Skill[]) => void;

  addLog: (entry: Omit<LogEntry, "id">) => void;
  clearLogs: () => void;

  handleEvent: (event: GatewayEventFrame) => void;
};

const SETTINGS_KEY = "command.ui-react.settings.v1";

function loadSettings(): { gatewayUrl: string; token: string } {
  const defaultUrl = (() => {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}/ws`;
  })();

  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) {
      return { gatewayUrl: defaultUrl, token: "" };
    }
    const parsed = JSON.parse(raw);
    return {
      gatewayUrl: typeof parsed.gatewayUrl === "string" ? parsed.gatewayUrl : defaultUrl,
      token: typeof parsed.token === "string" ? parsed.token : "",
    };
  } catch {
    return { gatewayUrl: defaultUrl, token: "" };
  }
}

function saveSettings(settings: { gatewayUrl: string; token: string }) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // best-effort
  }
}

let logIdCounter = 0;

export const useGatewayStore = create<GatewayState>((set, get) => {
  const initial = loadSettings();

  return {
    status: "disconnected",
    hello: null,
    gatewayUrl: initial.gatewayUrl,
    token: initial.token,
    channels: [],
    sessions: [],
    skills: [],
    logs: [],

    setStatus: (status) => set({ status }),
    setHello: (hello) => set({ hello }),

    setGatewayUrl: (gatewayUrl) => {
      set({ gatewayUrl });
      saveSettings({ gatewayUrl, token: get().token });
    },

    setToken: (token) => {
      set({ token });
      saveSettings({ gatewayUrl: get().gatewayUrl, token });
    },

    setChannels: (channels) => set({ channels }),
    updateChannel: (id, updates) =>
      set((state) => ({
        channels: state.channels.map((ch) => (ch.id === id ? { ...ch, ...updates } : ch)),
      })),

    setSessions: (sessions) => set({ sessions }),
    addSession: (session) =>
      set((state) => ({
        sessions: [...state.sessions.filter((s) => s.id !== session.id), session],
      })),
    removeSession: (id) =>
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
      })),

    setSkills: (skills) => set({ skills }),

    addLog: (entry) =>
      set((state) => ({
        logs: [...state.logs.slice(-999), { ...entry, id: `log-${++logIdCounter}` }],
      })),
    clearLogs: () => set({ logs: [] }),

    handleEvent: (event) => {
      const state = get();

      // Handle common events
      if (event.event === "channels.updated") {
        const payload = event.payload as { channels?: Channel[] };
        if (payload?.channels) {
          state.setChannels(payload.channels);
        }
      } else if (event.event === "sessions.updated") {
        const payload = event.payload as { sessions?: Session[] };
        if (payload?.sessions) {
          state.setSessions(payload.sessions);
        }
      } else if (event.event === "skills.updated") {
        const payload = event.payload as { skills?: Skill[] };
        if (payload?.skills) {
          state.setSkills(payload.skills);
        }
      } else if (event.event === "log") {
        const payload = event.payload as {
          level?: string;
          message?: string;
          source?: string;
          data?: unknown;
        };
        if (payload?.message) {
          state.addLog({
            level: (payload.level as LogEntry["level"]) ?? "info",
            message: payload.message,
            timestamp: Date.now(),
            source: payload.source,
            data: payload.data,
          });
        }
      }
    },
  };
});
